import React, { memo, useCallback, useEffect, useMemo } from 'react';

/**
 * Generate an image tag with a srcset attribute to load images in a responsive way
 *
 * @param {Function} onLoad
 *        A function to call when the image has loaded
 *
 * @param {String} src
 *        The URL of the image
 *
 * @param {Number} minWidth
 *        The minimum width of the image in pixels
 *        Will be rounded to the nearest step
 *        (defaults to 48)
 *
 * @param {Number} maxWidth
 *        The maximum width of the image in pixels
 *        (defaults to the natural width of the image)
 * 
 * @param {Number} step
 *        The step size in pixels
 *        (defaults to 48)
 *
 * @param {String} sizes
 *        The sizes attribute for the image
 *        (defaults to 'auto')
 * 
 * @param {String} loading
 *        The loading attribute for the image
 *        (defaults to 'lazy')
 * 
 * @param {String} crop
 *        The crop attribute for the image
 * 
 * @param {Number} width
 *        The natural width of the image in pixels
 *        (Required)
 * 
 * @param {Number} height
 *        The natural height of the image in pixels
 * 
 * @param {Object} otherProps
 *        Any other props to pass to the image tag
 *
 * @returns {String}
 */

const Rimg = memo(function(props) {
  const imgRef = React.createRef();
  let {src, minWidth, maxWidth, step, sizes, loading, fetchPriority, crop, width, height, ...otherProps } = props;
  sizes = sizes || 'auto';
  loading = loading || 'lazy';
   
  const onImageLoad = useCallback((e) => {
    props.onLoad && props.onLoad(e);
  }, [props.onLoad]);

  useEffect(() => {
    imgRef.current.addEventListener('load', onImageLoad);
    
    return () => {
      if (imgRef.current) {
        imgRef.current.removeEventListener('load', onImageLoad);
      }
    }
  }, [imgRef.current, onImageLoad])

  const srcSet = useMemo(() => {
    step = step || 48;
    minWidth = Math.ceil(minWidth / step) * step;
    maxWidth = maxWidth || width;
    let srcSet = [];
    let currentWidth = minWidth || 48;
    if (crop && height) {
      const ratio = width / height;
      let currentHeight = Math.ceil(currentWidth / ratio);
      while (currentHeight <= height && currentWidth <= maxWidth) {
        srcSet.push(`${src}&width=${currentWidth}&height=${currentHeight}&crop=${crop} ${currentWidth}w`);
        currentWidth += step;
        currentHeight = Math.ceil(currentWidth / ratio);
      }

      return srcSet.join(', ');
    }
    while (currentWidth <= maxWidth) {
      srcSet.push(`${src}&width=${currentWidth} ${currentWidth}w`);
      currentWidth += step;
    }
    if (currentWidth - step < maxWidth) {
      srcSet.push(`${src}&width=${maxWidth} ${maxWidth}w`);
    }
    return srcSet.join(', ');
  }, [src, minWidth, maxWidth, step, crop, width, height]);

  return (
    <img ref={imgRef} sizes={sizes} loading={loading} fetchpriority={fetchPriority} srcSet={srcSet} width={width} height={height} {...otherProps} />
  )
})

export default Rimg